import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, queryOptions } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { T, sans, card, linkBtn } from './VendorDetails';
import type { VendorTab } from './VendorDetails';
import ScanTypeButton, { ScanDepth } from '../../components/ScanTypeButton';

/* ── Types ─────────────────────────────────────────────────────────── */
type Decision = 'approved' | 'approved_with_conditions' | 'rejected';
interface PendingSend {
  id: string; questionnaire_name: string | null; recipient_name: string | null; recipient_email: string | null;
  status: 'sent' | 'opened'; sent_at: string | null; due_date: string | null; answered_count: number; total_items: number;
}
interface Checklist {
  scan: { done: boolean; date: string | null; scan_id: string | null; score: number | null; risk_level: string | null;
          latest: { scan_id: string; date: string; score: number | null } | null };
  questionnaire: { done: boolean; date: string | null; response_id: string | null; questionnaire_name: string | null;
                   recipient: string | null; answered_count: number | null; total_items: number | null; status: string | null;
                   pending: PendingSend[] };
  monitoring: { done: boolean; date: string | null; via: 'continuous' | 'schedule' | null; paused: boolean;
                schedule: { frequency: string | null; next_run: string | null } | null };
}
interface TimelineEvent { at: string; kind: string; title: string; detail: string | null; scan_id?: string }
interface AssessmentRow {
  id: string; status: 'in_progress' | 'completed'; started_at: string; completed_at: string | null; cadence_months: number;
  scan_id: string | null; scan_completed_at: string | null; scan_score: number | null; scan_risk_level: string | null;
  questionnaire_response_id: string | null; questionnaire_completed_at: string | null; questionnaire_name: string | null;
  monitoring_confirmed_at: string | null; started_by_name: string | null;
  decision: Decision | null; decision_notes: string | null; decided_at: string | null; decided_by_name: string | null;
}
interface ActiveAssessment extends AssessmentRow { checklist: Checklist; timeline: TimelineEvent[] }
interface Props {
  vendorId: string; vendorName?: string; isDark: boolean;
  onOpenTab?: (tab: VendorTab) => void; onNewScan?: (depth: ScanDepth) => void;
}
type Row = AssessmentRow & { number: number; live?: ActiveAssessment };

/* ── Helpers ───────────────────────────────────────────────────────── */
const DAY = 86400000;
const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg).filter(Boolean).join('; ') || fallback;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  return fallback;
};
const fmtDate = (iso: string | Date | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const addMonths = (iso: string, months: number) => { const d = new Date(iso); d.setMonth(d.getMonth() + months); return d; };
const daysBetween = (a: string | Date, b: string | Date) => Math.round((new Date(b).getTime() - new Date(a).getTime()) / DAY);
const relative = (iso: string | null | undefined) => {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / DAY);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  return days < 60 ? `${days} days ago` : `${Math.round(days / 30)} months ago`;
};
const cadenceLabel = (m: number) => m % 12 === 0 ? (m === 12 ? 'Every year' : `Every ${m / 12} years`) : `Every ${m} months`;
const csvCell = (v: unknown) => {  // cells starting with = + - @ would run as formulas in a spreadsheet
  let s = v == null ? '' : String(v);
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const Icon = ({ d, size = 14, style }: { d: string; size?: number; style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }}><path d={d} /></svg>
);
const I = {
  check: 'M5 12l5 5L20 7', plus: 'M12 5v14M5 12h14', close: 'M6 6l12 12M18 6 6 18', chevron: 'M9 6l6 6-6 6',
  download: 'M12 3v12M7 10l5 5 5-5M5 21h14', edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z',
  up: 'M18 15l-6-6-6 6', down: 'M6 9l6 6 6-6',
};

type Tone = 'green' | 'amber' | 'red' | 'blue' | 'slate';
const TONES: Record<Tone, { fg: string; bg: string; darkFg: string }> = {
  green: { fg: '#15803D', bg: '#DCFCE7', darkFg: '#4ADE80' },
  amber: { fg: '#B45309', bg: '#FEF3C7', darkFg: '#FBBF24' },
  red: { fg: '#B91C1C', bg: '#FEE2E2', darkFg: '#F87171' },
  blue: { fg: '#1D4ED8', bg: '#DBEAFE', darkFg: '#60A5FA' },
  slate: { fg: '#475569', bg: '#F1F5F9', darkFg: '#94A3B8' },
};
const DECISION_META: Record<Decision, { label: string; tone: Tone; hint: string }> = {
  approved: { label: 'Approved', tone: 'green', hint: 'Cleared to continue the relationship.' },
  approved_with_conditions: { label: 'Approved with conditions', tone: 'amber', hint: 'Continue, subject to conditions you list.' },
  rejected: { label: 'Rejected', tone: 'red', hint: 'Risk not acceptable at this time.' },
};
const riskTone = (level: string | null | undefined): Tone => {
  const l = (level || '').toLowerCase();
  return l === 'low' ? 'green' : l === 'medium' ? 'amber' : l === 'high' || l === 'critical' ? 'red' : 'slate';
};

const queryOpts = (vendorId: string) => queryOptions({
  queryKey: ['vendors', 'detail', vendorId, 'assessments'],
  queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/assessments`)).data as { active: ActiveAssessment; history: AssessmentRow[] },
});

/* ── Page ──────────────────────────────────────────────────────────── */
export default function VendorAssessments({ vendorId, vendorName, isDark, onOpenTab, onNewScan }: Props) {
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';
  const navigate = useNavigate();
  const query = useQuery(queryOpts(vendorId));

  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [aboutOpen, setAboutOpen] = useState(true);
  const [toast, setToast] = useState<{ text: string; error?: boolean } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const c = {
    ink: isDark ? '#e2e8f0' : T.ink, muted: isDark ? '#94a3b8' : T.slate, faint: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr, soft: isDark ? '#141414' : T.surface2, surface: isDark ? '#0d0d0d' : T.surface,
    accent: isDark ? '#60A5FA' : T.blue, primary: isDark ? '#3B82F6' : T.ink, red: isDark ? '#F87171' : T.red,
    green: isDark ? TONES.green.darkFg : '#16A34A', amber: isDark ? TONES.amber.darkFg : '#D97706',
  };
  const chip = (tone: Tone): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, fontFamily: sans,
    color: isDark ? TONES[tone].darkFg : TONES[tone].fg, background: isDark ? `${TONES[tone].fg}2E` : TONES[tone].bg, whiteSpace: 'nowrap',
  });
  const btn = (primary = false): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
    border: `1px solid ${primary ? c.primary : c.line}`, background: primary ? c.primary : c.surface, color: primary ? '#fff' : c.ink, whiteSpace: 'nowrap',
  });
  const ui: UI = { c, chip, btn, isDark };

  const invalidates = [['vendors', 'detail', vendorId, 'assessments']];
  const startMutation = useMutation({ mutationFn: () => api.post(`/v1/vendors/${vendorId}/assessments/start`), meta: { invalidates } });
  const cadenceMutation = useMutation({
    mutationFn: ({ id, months }: { id: string; months: number }) => api.patch(`/v1/vendors/${vendorId}/assessments/${id}/cadence`, { cadence_months: months }),
    meta: { invalidates },
  });
  const decisionMutation = useMutation({
    mutationFn: ({ id, decision, notes }: { id: string; decision: Decision | null; notes: string }) =>
      api.patch(`/v1/vendors/${vendorId}/assessments/${id}/decision`, { decision, notes }),
    meta: { invalidates },
  });

  // One row per assessment, newest first: the open one (if any) followed by every completed one.
  const rows: Row[] = useMemo(() => {
    if (!query.data) return [];
    const { active, history } = query.data;
    const done: Row[] = history.map((h, i) => ({ ...h, number: history.length - i, live: h.id === active.id ? active : undefined }));
    return active.status === 'in_progress' ? [{ ...active, number: history.length + 1, live: active }, ...done] : done;
  }, [query.data]);

  if (query.isPending) {
    return <div style={{ ...card(isDark), textAlign: 'center', padding: '48px 0', fontSize: 12.5, color: c.muted, fontFamily: sans }}>Loading assessments…</div>;
  }
  if (query.isError || !query.data) {
    return (
      <div style={{ ...card(isDark), textAlign: 'center', padding: '48px 0', fontSize: 12.5, color: c.red, fontFamily: sans }}>
        {apiError(query.error, 'Could not load assessments.')} <button style={linkBtn(isDark)} onClick={() => query.refetch()}>Retry</button>
      </div>
    );
  }

  const { active } = query.data;
  const hasOpen = active.status === 'in_progress';
  const counts = { all: rows.length, pending: rows.filter(r => r.status === 'in_progress').length, completed: rows.filter(r => r.status === 'completed').length };
  const visible = rows.filter(r => filter === 'all' || (filter === 'pending' ? r.status === 'in_progress' : r.status === 'completed'));
  const opened = rows.find(r => r.id === openId) || null;

  const startAssessment = async () => {
    try {
      const { data } = await startMutation.mutateAsync();
      setToast({ text: `Assessment #${counts.completed + 1} started` });
      setOpenId(data?.id ?? null);
    } catch (e) { setToast({ text: apiError(e, 'Could not start a new assessment.'), error: true }); }
  };
  const exportCsv = () => {
    const out = [['Assessment', 'Status', 'Started', 'Started by', 'Completed', 'Reassessment due', 'Risk score', 'Risk level', 'Questionnaire', 'Decision', 'Decision notes'],
      ...rows.map(r => [`Assessment #${r.number}`, r.status === 'in_progress' ? 'Pending' : 'Completed', fmtDate(r.started_at), r.started_by_name ?? '',
        fmtDate(r.completed_at), r.completed_at ? fmtDate(addMonths(r.completed_at, r.cadence_months)) : '', r.scan_score ?? '', r.scan_risk_level ?? '',
        r.questionnaire_name ?? '', r.decision ? DECISION_META[r.decision].label : '', r.decision_notes ?? ''])];
    const blob = new Blob([out.map(r => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(vendorName || 'vendor').replace(/[^\w-]+/g, '_')}_assessments.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: c.faint, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `1px solid ${c.line}`, whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '13px 16px', borderBottom: `1px solid ${c.line}`, fontSize: 12.5, color: c.muted, whiteSpace: 'nowrap', verticalAlign: 'middle' };

  return (
    <div style={{ ...card(isDark), padding: 0, fontFamily: sans, overflow: 'hidden' }}>
      <style>{`
        @keyframes vaDrawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes vaFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        .va-row { cursor: pointer; transition: background .1s; }
        .va-row:hover { background: ${c.soft}; }
        .va-link:hover { text-decoration: underline; }
      `}</style>

      {/* header */}
      <div style={{ padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: c.ink }}>Assessments</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btn(), opacity: rows.length ? 1 : 0.5 }} disabled={!rows.length} onClick={exportCsv}><Icon d={I.download} size={13} />Export</button>
            {canEdit && (
              <button style={{ ...btn(true), opacity: hasOpen || startMutation.isPending ? 0.5 : 1, cursor: hasOpen ? 'not-allowed' : 'pointer' }}
                disabled={hasOpen || startMutation.isPending} onClick={startAssessment}
                title={hasOpen ? 'Complete the pending assessment before starting another' : undefined}>
                <Icon d={I.plus} size={13} />{startMutation.isPending ? 'Starting…' : 'Start assessment'}
              </button>
            )}
          </div>
        </div>
        {aboutOpen && (
          <div style={{ fontSize: 12.5, color: c.muted, lineHeight: 1.6, marginTop: 8, maxWidth: 720 }}>
            An assessment is one complete due-diligence review of {vendorName || 'this vendor'}. It stays <strong style={{ color: c.ink }}>pending</strong> until
            three things are in place since it started: a completed security scan, an answered questionnaire, and continuous monitoring or a scan
            schedule. It then completes on its own, and is due again after its reassessment period.
          </div>
        )}
        <button onClick={() => setAboutOpen(o => !o)} style={{ ...linkBtn(isDark), fontSize: 12, padding: 0, marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {aboutOpen ? 'Collapse' : 'What is an assessment?'}<Icon d={aboutOpen ? I.up : I.down} size={12} />
        </button>
      </div>

      {/* filter */}
      <div style={{ padding: '0 20px 14px', display: 'flex', gap: 0 }}>
        {(['all', 'pending', 'completed'] as const).map((f, i) => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '7px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
            border: `1px solid ${filter === f ? c.primary : c.line}`, marginLeft: i ? -1 : 0, position: 'relative', zIndex: filter === f ? 1 : 0,
            borderRadius: i === 0 ? '7px 0 0 7px' : i === 2 ? '0 7px 7px 0' : 0,
            background: filter === f ? c.primary : c.surface, color: filter === f ? '#fff' : c.ink,
          }}>
            {f === 'all' ? 'All' : f === 'pending' ? 'Pending' : 'Completed'}
            <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.75 }}>{counts[f]}</span>
          </button>
        ))}
      </div>

      {/* table */}
      <div style={{ overflowX: 'auto', borderTop: `1px solid ${c.line}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead>
            <tr>
              {['Name', 'Status', 'Requirements', 'Started by', 'Started', 'Completed', 'Reassessment due', ''].map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={8} style={{ ...td, textAlign: 'center', padding: '36px 16px', color: c.faint }}>
                {filter === 'completed' ? 'No completed assessments yet.' : 'No pending assessments.'}
              </td></tr>
            ) : visible.map(r => {
              const done = r.status === 'completed';
              const met = r.live && !done
                ? [r.live.checklist.scan.done, r.live.checklist.questionnaire.done, r.live.checklist.monitoring.done]
                : [!!r.scan_id, !!r.questionnaire_response_id, !!r.monitoring_confirmed_at];
              const due = r.completed_at ? addMonths(r.completed_at, r.cadence_months) : null;
              // Only the newest assessment can be overdue -- an older one has already been followed by another.
              const dueIn = due && r.id === rows[0]?.id ? Math.ceil((due.getTime() - Date.now()) / DAY) : null;
              return (
                <tr key={r.id} className="va-row" onClick={() => setOpenId(r.id)} style={{ background: openId === r.id ? c.soft : undefined }}>
                  <td style={td}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: c.ink }}>Assessment #{r.number}</div>
                    <div style={{ fontSize: 11.5, color: c.faint, marginTop: 1 }}>{cadenceLabel(r.cadence_months)}</div>
                  </td>
                  <td style={td}>
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      {done ? <span style={chip('green')}>Completed</span> : <span style={chip('amber')}>Pending</span>}
                      {r.decision && <span style={chip(DECISION_META[r.decision].tone)}>{DECISION_META[r.decision].label}</span>}
                    </span>
                  </td>
                  <td style={td}><RequirementBar ui={ui} met={met} /></td>
                  <td style={{ ...td, color: c.ink }}>{r.started_by_name || '—'}</td>
                  <td style={td}>{fmtDate(r.started_at)}</td>
                  <td style={td}>{done ? fmtDate(r.completed_at) : '—'}</td>
                  <td style={td}>
                    {due ? <span style={{ color: dueIn !== null && dueIn < 0 ? c.red : c.muted, fontWeight: dueIn !== null && dueIn < 0 ? 600 : 400 }}>
                      {fmtDate(due)}{dueIn !== null && dueIn < 0 ? ' · overdue' : ''}</span> : '—'}
                  </td>
                  <td style={{ ...td, width: 28, color: c.faint }}><Icon d={I.chevron} size={15} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {opened && (
        <AssessmentDrawer ui={ui} row={opened} vendorName={vendorName} canEdit={canEdit}
          onClose={() => setOpenId(null)}
          onNewScan={depth => onNewScan?.(depth)}
          onOpenTab={tab => { setOpenId(null); onOpenTab?.(tab); }}
          onViewScan={id => navigate(`/scans/${id}/results`)}
          onCadence={async months => {
            try { await cadenceMutation.mutateAsync({ id: opened.id, months }); setToast({ text: `Reassess ${cadenceLabel(months).toLowerCase()}` }); }
            catch (e) { setToast({ text: apiError(e, 'Could not update the cadence.'), error: true }); }
          }}
          savingDecision={decisionMutation.isPending}
          onDecision={async (decision, notes) => {
            try {
              await decisionMutation.mutateAsync({ id: opened.id, decision, notes });
              setToast({ text: decision ? `Marked ${DECISION_META[decision].label.toLowerCase()}` : 'Decision cleared' });
              return true;
            } catch (e) { setToast({ text: apiError(e, 'Could not save the decision.'), error: true }); return false; }
          }} />
      )}

      {toast && (
        <div role="status" style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, padding: '9px 16px', borderRadius: 8,
          background: toast.error ? T.red : (isDark ? '#1e293b' : T.ink), color: '#fff', fontSize: 12.5, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 8, animation: 'vaFade .2s ease-out', fontFamily: sans,
        }}><Icon d={toast.error ? I.close : I.check} size={13} />{toast.text}</div>
      )}
    </div>
  );
}

/* ── Pieces ────────────────────────────────────────────────────────── */
type UI = { c: any; chip: (t: Tone) => React.CSSProperties; btn: (p?: boolean) => React.CSSProperties; isDark: boolean };

function RequirementBar({ ui, met }: { ui: UI; met: boolean[] }) {
  const { c } = ui;
  const n = met.filter(Boolean).length;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ display: 'inline-flex', gap: 3 }}>
        {met.map((m, i) => <span key={i} style={{ width: 18, height: 6, borderRadius: 3, background: m ? c.green : c.line }} />)}
      </span>
      <span style={{ fontSize: 12, color: n === 3 ? c.ink : c.muted, fontWeight: 600 }}>{n} of 3</span>
    </span>
  );
}

function AssessmentDrawer({ ui, row, vendorName, canEdit, onClose, onNewScan, onOpenTab, onViewScan, onCadence, savingDecision, onDecision }: {
  ui: UI; row: Row; vendorName?: string; canEdit: boolean; onClose: () => void; onNewScan: (depth: ScanDepth) => void;
  onOpenTab: (t: VendorTab) => void; onViewScan: (id: string) => void; onCadence: (m: number) => Promise<void>;
  savingDecision: boolean; onDecision: (d: Decision | null, notes: string) => Promise<boolean>;
}) {
  const { c, chip } = ui;
  const panelRef = useRef<HTMLDivElement>(null);
  const done = row.status === 'completed';
  const live = row.live;
  const [cadenceEdit, setCadenceEdit] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => { if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); clearTimeout(t); };
  }, [onClose]);

  // Requirement state: live evaluation for the open/current assessment, stored evidence for older ones.
  const scan = live?.checklist.scan;
  const q = live?.checklist.questionnaire;
  const m = live?.checklist.monitoring;
  const scanDone = done ? !!row.scan_id : !!scan?.done;
  const qDone = done ? !!row.questionnaire_response_id : !!q?.done;
  const mDone = done ? !!row.monitoring_confirmed_at : !!m?.done;
  const metCount = [scanDone, qDone, mDone].filter(Boolean).length;
  const due = row.completed_at ? addMonths(row.completed_at, row.cadence_months) : null;
  const pending = !done ? q?.pending ?? [] : [];

  const section: React.CSSProperties = { padding: '16px 20px', borderBottom: `1px solid ${c.line}` };
  const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: c.faint, textTransform: 'uppercase', letterSpacing: '.04em' };
  const action = (text: string, onClick: () => void) => canEdit && (
    <button onClick={onClick} style={{ padding: '5px 11px', borderRadius: 6, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans, border: `1px solid ${c.line}`, background: c.surface, color: c.ink, whiteSpace: 'nowrap' }}>{text}</button>
  );
  const link = (text: string, onClick: () => void) => (
    <button className="va-link" onClick={onClick} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: c.accent, fontSize: 12, fontWeight: 600, fontFamily: sans, whiteSpace: 'nowrap' }}>{text}</button>
  );

  return (
    <div ref={panelRef} role="dialog" aria-modal="true" aria-label={`Assessment #${row.number}`} style={{
      position: 'fixed', top: 64, right: 0, bottom: 0, width: 'min(520px, 100vw)', zIndex: 1300, background: c.surface,
      borderLeft: `1px solid ${c.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans, animation: 'vaDrawerIn .2s ease-out',
    }}>
      {/* title */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          {vendorName && <div style={{ fontSize: 11.5, color: c.faint, fontWeight: 600 }}>{vendorName}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: c.ink }}>Assessment #{row.number}</span>
            {done ? <span style={chip('green')}>Completed</span> : <span style={chip('amber')}>Pending · {metCount} of 3</span>}
          </div>
        </div>
        <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', color: c.muted, cursor: 'pointer', padding: 4 }}><Icon d={I.close} size={16} /></button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* details */}
        <div style={{ ...section, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          {[
            ['Started', `${fmtDate(row.started_at)}${row.started_by_name ? ` · ${row.started_by_name}` : ''}`],
            ['Completed', done ? `${fmtDate(row.completed_at)} · ${Math.max(0, daysBetween(row.started_at, row.completed_at!))} days` : 'Not yet'],
            ['Reassessment due', due ? fmtDate(due) : 'After completion'],
          ].map(([k, v]) => (
            <div key={k}><div style={label}>{k}</div><div style={{ fontSize: 12.5, color: c.ink, marginTop: 4 }}>{v}</div></div>
          ))}
          <div>
            <div style={label}>Reassessment period</div>
            {cadenceEdit ? (
              <select autoFocus defaultValue={row.cadence_months} onBlur={() => setCadenceEdit(false)}
                onChange={async e => { setCadenceEdit(false); await onCadence(Number(e.target.value)); }}
                style={{ marginTop: 3, padding: '4px 6px', fontSize: 12.5, fontFamily: sans, color: c.ink, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6 }}>
                {[3, 6, 12, 24].map(mo => <option key={mo} value={mo}>{cadenceLabel(mo)}</option>)}
              </select>
            ) : (
              <div style={{ fontSize: 12.5, color: c.ink, marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
                {cadenceLabel(row.cadence_months)}
                {canEdit && link('Change', () => setCadenceEdit(true))}
              </div>
            )}
          </div>
        </div>

        {/* what's needed */}
        <div style={section}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>{done ? 'Completed requirements' : 'To complete this assessment'}</div>
            <RequirementBar ui={ui} met={[scanDone, qDone, mDone]} />
          </div>

          <Requirement ui={ui} done={scanDone} title="Security scan"
            detail={scanDone
              ? <>Scanned {fmtDate(done ? row.scan_completed_at : scan?.date)} · <strong style={{ color: c.ink }}>{(done ? row.scan_score : scan?.score) ?? '—'}/100</strong>
                  {(done ? row.scan_risk_level : scan?.risk_level) && <span style={{ ...chip(riskTone(done ? row.scan_risk_level : scan?.risk_level)), marginLeft: 6 }}>{done ? row.scan_risk_level : scan?.risk_level}</span>}</>
              : <>No scan has completed since this assessment started.{scan?.latest ? ` The last one (${fmtDate(scan.latest.date)}) was before it began.` : ''}</>}
            right={scanDone ? ((done ? row.scan_id : scan?.scan_id) && link('View report', () => onViewScan((done ? row.scan_id : scan?.scan_id)!))) : (canEdit ? <ScanTypeButton label="Run scan" onPick={onNewScan} isDark={ui.isDark} align="left" buttonStyle={{ padding: '5px 11px', borderRadius: 6, fontSize: 11.5, fontWeight: 600, fontFamily: sans, border: `1px solid ${c.line}`, background: c.surface, color: c.ink, whiteSpace: 'nowrap' }} /> : null)} />

          <Requirement ui={ui} done={qDone} title="Questionnaire answered"
            detail={qDone
              ? <>{(done ? row.questionnaire_name : q?.questionnaire_name) || 'Questionnaire'} · answered {fmtDate(done ? row.questionnaire_completed_at : q?.date)}{!done && q?.recipient ? ` by ${q.recipient}` : ''}</>
              : pending.length ? <>{pending.length} sent, waiting for the vendor to answer.</> : <>No questionnaire has been answered since this assessment started.</>}
            right={qDone ? link('Open', () => onOpenTab('questionnaires')) : pending.length ? link('Follow up', () => onOpenTab('questionnaires')) : action('Send questionnaire', () => onOpenTab('questionnaires'))}>
            {pending.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {pending.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: `1px solid ${c.line}`, borderRadius: 7 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.questionnaire_name || 'Questionnaire'}</div>
                      <div style={{ fontSize: 11, color: c.faint, marginTop: 1 }}>
                        {p.recipient_name || p.recipient_email} · sent {relative(p.sent_at)}{p.total_items ? ` · ${p.answered_count}/${p.total_items} answered` : ''}
                      </div>
                    </div>
                    <span style={chip(p.status === 'opened' ? 'blue' : 'slate')}>{p.status === 'opened' ? 'Opened' : 'Sent'}</span>
                  </div>
                ))}
              </div>
            )}
          </Requirement>

          <Requirement ui={ui} done={mDone} title="Monitoring in place" last
            detail={mDone
              ? (done ? <>Confirmed {fmtDate(row.monitoring_confirmed_at)}</>
                 : m?.via === 'schedule' ? <>Scheduled scans ({m.schedule?.frequency || 'custom'}){m.schedule?.next_run ? ` · next ${fmtDate(m.schedule.next_run)}` : ''}</>
                 : <>Continuous monitoring is on.</>)
              : <>Monitoring is paused and no scan schedule is set.</>}
            right={!mDone ? action('Set up', () => onOpenTab('schedule')) : !done ? link('Schedule', () => onOpenTab('schedule')) : null} />
        </div>

        {/* decision */}
        {done && <DecisionSection ui={ui} row={row} canEdit={canEdit} saving={savingDecision} onSave={onDecision} />}

        {/* activity */}
        {live && live.timeline.length > 0 && (
          <div style={{ ...section, borderBottom: 'none' }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: c.ink, marginBottom: 12 }}>Activity</div>
            {live.timeline.map((e, i) => (
              <div key={`${e.kind}-${e.at}-${i}`} style={{ display: 'flex', gap: 10, position: 'relative', paddingBottom: i === live.timeline.length - 1 ? 0 : 12 }}>
                {i < live.timeline.length - 1 && <span style={{ position: 'absolute', left: 3.5, top: 11, bottom: 0, width: 1, background: c.line }} />}
                <span style={{ width: 8, height: 8, borderRadius: '50%', marginTop: 4, flexShrink: 0, background: e.kind === 'completed' ? c.green : e.kind === 'scan' || e.kind.startsWith('q_') ? c.accent : c.faint }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: c.ink }}>{e.title}</div>
                  <div style={{ fontSize: 11, color: c.faint, marginTop: 1 }}>
                    {fmtDateTime(e.at)}{e.detail ? ` · ${e.detail}` : ''}
                    {e.scan_id && <> · {link('View', () => onViewScan(e.scan_id!))}</>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Requirement({ ui, done, title, detail, right, last, children }: {
  ui: UI; done: boolean; title: string; detail: React.ReactNode; right?: React.ReactNode; last?: boolean; children?: React.ReactNode;
}) {
  const { c } = ui;
  return (
    <div style={{ display: 'flex', gap: 11, padding: '11px 0', borderBottom: last ? 'none' : `1px solid ${c.line}` }}>
      <span style={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `2px solid ${done ? c.green : c.amber}`, background: done ? c.green : 'transparent', color: '#fff',
      }}>{done && <Icon d={I.check} size={11} />}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: c.ink }}>{title}</span>
          {right}
        </div>
        <div style={{ fontSize: 12, color: c.muted, marginTop: 3, lineHeight: 1.5 }}>{detail}</div>
        {children}
      </div>
    </div>
  );
}

function DecisionSection({ ui, row, canEdit, saving, onSave }: {
  ui: UI; row: Row; canEdit: boolean; saving: boolean; onSave: (d: Decision | null, notes: string) => Promise<boolean>;
}) {
  const { c, chip, btn, isDark } = ui;
  const [editing, setEditing] = useState(!row.decision);
  const [choice, setChoice] = useState<Decision | null>(row.decision);
  const [notes, setNotes] = useState(row.decision_notes || '');
  useEffect(() => { setEditing(!row.decision); setChoice(row.decision); setNotes(row.decision_notes || ''); }, [row.id, row.decision, row.decision_notes]);
  const needsNotes = choice === 'approved_with_conditions' && !notes.trim();

  return (
    <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.line}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: c.ink }}>Decision</span>
        {!editing && canEdit && row.decision && (
          <button className="va-link" onClick={() => setEditing(true)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: c.accent, fontSize: 12, fontWeight: 600, fontFamily: sans, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Icon d={I.edit} size={12} />Change
          </button>
        )}
      </div>
      {!editing && row.decision ? (
        <>
          <span style={chip(DECISION_META[row.decision].tone)}>{DECISION_META[row.decision].label}</span>
          {row.decision_notes && <div style={{ fontSize: 12.5, color: c.ink, marginTop: 8, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{row.decision_notes}</div>}
          <div style={{ fontSize: 11.5, color: c.faint, marginTop: 6 }}>Recorded {fmtDate(row.decided_at)}{row.decided_by_name ? ` by ${row.decided_by_name}` : ''}</div>
        </>
      ) : !canEdit ? (
        <div style={{ fontSize: 12.5, color: c.muted }}>No decision recorded.</div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: c.muted, marginBottom: 10 }}>Record whether {row.decision ? 'this vendor is' : 'the vendor is'} cleared after this assessment.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(Object.keys(DECISION_META) as Decision[]).map(d => {
              const meta = DECISION_META[d];
              const on = choice === d;
              const col = isDark ? TONES[meta.tone].darkFg : TONES[meta.tone].fg;
              return (
                <label key={d} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 7, cursor: 'pointer', border: `1px solid ${on ? col : c.line}` }}>
                  <input type="radio" name={`decision-${row.id}`} checked={on} onChange={() => setChoice(d)} style={{ accentColor: col, margin: 0 }} />
                  <span style={{ flex: 1 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: c.ink }}>{meta.label}</span>
                    <span style={{ display: 'block', fontSize: 11.5, color: c.muted, marginTop: 1 }}>{meta.hint}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} maxLength={4000}
            placeholder={choice === 'approved_with_conditions' ? 'List the conditions, e.g. "Share the SOC 2 report within 60 days."' : 'Notes (optional)'}
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 10, padding: '8px 10px', fontSize: 12.5, fontFamily: sans, color: c.ink, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 7, outline: 'none', resize: 'vertical' }} />
          {needsNotes && <div style={{ fontSize: 11.5, color: c.amber, marginTop: 4 }}>Describe the conditions before saving.</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
            {row.decision && <button style={btn()} disabled={saving} onClick={() => { setEditing(false); setChoice(row.decision); setNotes(row.decision_notes || ''); }}>Cancel</button>}
            {row.decision && <button style={{ ...btn(), color: c.red }} disabled={saving} onClick={async () => { if (await onSave(null, '')) setEditing(true); }}>Clear</button>}
            <button style={{ ...btn(true), opacity: !choice || needsNotes || saving ? 0.6 : 1 }} disabled={!choice || needsNotes || saving}
              onClick={async () => { if (choice && await onSave(choice, notes)) setEditing(false); }}>{saving ? 'Saving…' : 'Save decision'}</button>
          </div>
        </>
      )}
    </div>
  );
}
